<template>
  <CModal
    :show.sync="showModal"
    :closeOnBackdrop="false"
    size="lg"
    color="primary"
  >
    <template #header>
      <h5 class="modal-title">Record Tax Payment</h5>
      <button
        type="button"
        class="close"
        @click="closeModal"
        :disabled="isSubmitting"
      >
        <span>&times;</span>
      </button>
    </template>

    <CForm @submit.prevent="submitPayment">
      <CRow>
        <!-- Payment Date -->
        <CCol md="6">
          <CInput
            label="Payment Date"
            type="date"
            v-model="form.paymentDate"
            :required="true"
            :disabled="isSubmitting"
          />
        </CCol>

        <!-- Payment Method -->
        <CCol md="6">
          <div class="form-group">
            <label>Payment Method <span class="text-danger">*</span></label>
            <select
              class="form-control"
              v-model="form.paymentMethod"
              required
              :disabled="isSubmitting"
            >
              <option value="">-- Select Method --</option>
              <option value="FPX">FPX Online Banking</option>
              <option value="Online Banking">Online Banking</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cheque">Cheque</option>
              <option value="Cash">Cash</option>
            </select>
          </div>
        </CCol>
      </CRow>

      <CRow>
        <!-- Tax Amount -->
        <CCol md="6">
          <CInput
            label="Tax Amount"
            type="number"
            step="0.01"
            min="0"
            v-model.number="form.taxAmount"
            :required="true"
            :disabled="isSubmitting"
            placeholder="0.00"
          >
            <template #prepend-content>
              <span>RM</span>
            </template>
          </CInput>
          <small class="text-muted">Net Tax Payable: {{ formatCurrency(period.netTaxPayable || 0) }}</small>
        </CCol>

        <!-- Penalty Amount -->
        <CCol md="6">
          <CInput
            label="Penalty Amount"
            type="number"
            step="0.01"
            min="0"
            v-model.number="form.penaltyAmount"
            :disabled="isSubmitting"
            placeholder="0.00"
          >
            <template #prepend-content>
              <span>RM</span>
            </template>
          </CInput>
          <small class="text-muted">Enter penalty if late payment</small>
        </CCol>
      </CRow>

      <!-- Total Amount (Computed) -->
      <CRow>
        <CCol md="12">
          <div class="alert alert-info mb-3">
            <div class="d-flex justify-content-between align-items-center">
              <strong>Total Payment Amount:</strong>
              <h4 class="mb-0 text-primary">{{ formatCurrency(totalAmount) }}</h4>
            </div>
          </div>
        </CCol>
      </CRow>

      <!-- Payment Reference -->
      <CRow>
        <CCol md="12">
          <CInput
            label="Payment Reference / Transaction ID"
            type="text"
            v-model="form.paymentReference"
            :required="true"
            :disabled="isSubmitting"
            placeholder="e.g., FPX123456789, Cheque No: 123456"
          />
        </CCol>
      </CRow>

      <!-- Notes -->
      <CRow>
        <CCol md="12">
          <div class="form-group">
            <label>Notes (Optional)</label>
            <textarea
              class="form-control"
              v-model="form.notes"
              rows="3"
              :disabled="isSubmitting"
              placeholder="Additional notes about this payment..."
            ></textarea>
          </div>
        </CCol>
      </CRow>

      <!-- Receipt Upload (Future Enhancement) -->
      <!-- <CRow>
        <CCol md="12">
          <div class="form-group">
            <label>Receipt / Proof of Payment (Optional)</label>
            <input
              type="file"
              class="form-control-file"
              accept="image/*,application/pdf"
              @change="handleFileUpload"
              :disabled="isSubmitting"
            />
            <small class="text-muted">Upload FPX receipt or payment proof (JPG, PNG, PDF)</small>
          </div>
        </CCol>
      </CRow> -->
    </CForm>

    <template #footer>
      <CButton
        color="secondary"
        @click="closeModal"
        :disabled="isSubmitting"
      >
        Cancel
      </CButton>
      <CButton
        color="primary"
        @click="submitPayment"
        :disabled="isSubmitting || !isFormValid"
      >
        <CSpinner
          v-if="isSubmitting"
          color="white"
          size="sm"
          class="mr-1"
        />
        {{ isSubmitting ? 'Processing...' : 'Record Payment' }}
      </CButton>
    </template>
  </CModal>
</template>

<script>
import TaxAPI from '@/lib/taxApi'

export default {
  name: 'TaxPaymentModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    period: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showModal: this.show,
      isSubmitting: false,
      form: {
        paymentDate: this.getTodayDate(),
        taxAmount: 0,
        penaltyAmount: 0,
        paymentMethod: '',
        paymentReference: '',
        notes: ''
      }
    }
  },
  computed: {
    totalAmount() {
      const tax = parseFloat(this.form.taxAmount) || 0
      const penalty = parseFloat(this.form.penaltyAmount) || 0
      return tax + penalty
    },
    isFormValid() {
      return (
        this.form.paymentDate &&
        this.form.taxAmount > 0 &&
        this.form.paymentMethod &&
        this.form.paymentReference
      )
    }
  },
  watch: {
    show(newVal) {
      this.showModal = newVal
      if (newVal) {
        this.resetForm()
        // Pre-fill tax amount with net tax payable
        this.form.taxAmount = this.period.netTaxPayable || 0
        // Pre-fill penalty amount if exists
        this.form.penaltyAmount = this.period.penaltyAmount || 0
      }
    },
    showModal(newVal) {
      this.$emit('update:show', newVal)
    }
  },
  methods: {
    getTodayDate() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    resetForm() {
      this.form = {
        paymentDate: this.getTodayDate(),
        taxAmount: 0,
        penaltyAmount: 0,
        paymentMethod: '',
        paymentReference: '',
        notes: ''
      }
      this.isSubmitting = false
    },
    closeModal() {
      if (!this.isSubmitting) {
        this.showModal = false
      }
    },
    formatCurrency(amount) {
      return 'RM ' + parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
    },
    async submitPayment() {
      if (!this.isFormValid || this.isSubmitting) {
        return
      }

      this.isSubmitting = true

      try {
        const token = window.localStorage.getItem('token')
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const taxApi = new TaxAPI(token)

        const paymentData = {
          taxPeriodId: this.period.id,
          paymentDate: new Date(this.form.paymentDate).toISOString(),
          taxAmount: parseFloat(this.form.taxAmount),
          penaltyAmount: parseFloat(this.form.penaltyAmount),
          paymentMethod: this.form.paymentMethod,
          paymentReference: this.form.paymentReference,
          notes: this.form.notes || null
        }

        const response = await taxApi.createPayment(paymentData)

        if (response.data && response.data.result) {
          this.$toast.success('Payment recorded', {
            position: 'top-right',
            duration: 3000
          })

          // Emit event with created payment
          this.$emit('payment-created', response.data.result)

          // Close modal
          this.showModal = false
        } else {
          throw new Error('Invalid response from server')
        }
      } catch (error) {
        console.error('Error recording payment:', error)

        let errorMessage = "Couldn't record the payment. Check your connection and try again."

        if (error.response) {
          if (error.response.data && error.response.data.message) {
            errorMessage = "Couldn't record the payment. " + error.response.data.message
          } else if (error.response.status === 401) {
            errorMessage = 'Your session has ended. Log in again to record the payment.'
          } else if (error.response.status === 404) {
            errorMessage = "This tax period no longer exists. Refresh the page and try again."
          }
        }

        this.$toast.error(errorMessage, {
          position: 'top-right',
          duration: 5000
        })
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.alert-info {
  background-color: #e3f2fd;
  border-color: #2196f3;
  color: #1565c0;
}

.text-muted {
  font-size: 0.875rem;
}

label {
  font-weight: 500;
}

.modal-title {
  font-weight: 600;
}
</style>
